import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { environment as env } from '@environments/environment';
import {
  CreateOrdersByOnboardingFileRequest,
  CreateOrdersByStorageRequest,
  DataFile,
  DelimiterType,
  EditOrdersRequest,
  ExportIndexingReportsRequest,
  ExportIndexingReportsResponse,
  ExtractedDocument,
  IndexingReportSummary,
  GetOrdersRequest,
  DeliveryAttempt,
  IndexedDocument,
  LoanListItem,
  OrderArchive,
  OrderLoanDocument,
  ServicingTransferOrder,
  ServicingTransferOrderDetail,
  Column,
  ColumnMapping,
  CoreTablesSchema,
  CreateMappingTemplateRequest,
  DealImportResults,
  DefinitionColumn,
  DocumentFileRow,
  ImportTemplate,
  ImportTemplateWithMappings,
  InvoiceFileRow,
  Loan,
  MapFileColumn,
  MappingDirection,
  OnboardingFileRow,
  Document,
  OrderDocument,
  OrderDocumentType,
  PayHistoryFileRow,
  UploadDocument,
  LoanDocumentWithActivity
} from './servicing-transfer.model';
import { map, Observable } from 'rxjs';
import { FileObject, GetLoansRequest, LoanStatusCount, PagedData } from '@shared/models';
import { buildQueryParams, mapToFile } from '@shared/utils';

@Injectable({ providedIn: 'root' })
export class ServicingTransferService {
  private readonly http = inject(HttpClient);

  // CMA Responses
  getCmaResponses = (orderDocumentId: number): Observable<MapFileColumn[]> =>
    this.http.get<MapFileColumn[]>(`${env.servicingTransferApi.cmaResponses}?orderDocumentId=${orderDocumentId}`);

  createCmaResponses = (
    orderDocumentId: number,
    cmaResponsesSelection: { id: number; isSelected: boolean }[]
  ): Observable<void> =>
    this.http.post<void>(`${env.servicingTransferApi.cmaResponses}?orderDocumentId=${orderDocumentId}`, {
      cmaResponsesSelection
    });

  updateCmaResponses = (cmaResponsesSelection: { id: number; isSelected: boolean }[]): Observable<void> =>
    this.http.put<void>(env.servicingTransferApi.cmaResponses, { cmaResponsesSelection });

  // Column Mappings
  downloadColumnMappings = (orderDocumentId: number): Observable<ColumnMapping[]> =>
    this.http.get<ColumnMapping[]>(
      `${env.servicingTransferApi.columnMappings}/download?orderDocumentId=${orderDocumentId}`
    );

  getColumnMappings = (orderDocumentId: number, isGenAiSkipped: boolean = false): Observable<ColumnMapping[]> =>
    this.http.get<ColumnMapping[]>(
      `${env.servicingTransferApi.columnMappings}?orderDocumentId=${orderDocumentId}&IsGenAiSkipped=${isGenAiSkipped}`
    );

  updateColumnMappings = (columnMappings: ColumnMapping[]): Observable<void> =>
    this.http.put<void>(env.servicingTransferApi.columnMappings, { columnMappings });

  // Columns
  getColumns = (orderDocumentId: number): Observable<Column[]> =>
    this.http
      .get<Column[]>(`${env.servicingTransferApi.columns}?orderDocumentId=${orderDocumentId}`)
      .pipe(map((columns) => columns.map((column, index) => ({ ...column, index }))));

  // Core Tables
  getCoreTablesSchema = (): Observable<CoreTablesSchema[]> =>
    this.http.get<CoreTablesSchema[]>(`${env.servicingTransferApi.loans}/fields`);

  // DDM Responses
  getColumnDefinitions = (
    orderDocumentId: number,
    isLlmMode: boolean,
    simulateResponse: boolean = false
  ): Observable<DefinitionColumn[]> =>
    this.http.get<DefinitionColumn[]>(
      `${env.servicingTransferApi.ddmResponses}?orderDocumentId=${orderDocumentId}&isLlmMode=${isLlmMode}&simulateResponse=${simulateResponse}`
    );

  updateColumnDefinition = (id: number, definition: string): Observable<void> =>
    this.http.put<void>(env.servicingTransferApi.ddmResponses, { id, definition });

  // Deal Import Results
  getDealImportResults = (orderDocumentId: number): Observable<DealImportResults> =>
    this.http.get<DealImportResults>(
      `${env.servicingTransferApi.dealImportResults}?orderDocumentId=${orderDocumentId}`
    );

  // Gen AI Mapping
  runGenAiMapping = (orderDocumentId: number): Observable<void> =>
    this.http.post<void>(`${env.servicingTransferApi.genAiMapping}/run?orderDocumentId=${orderDocumentId}`, {});

  // Loan Documents
  removeLoanDocument = (documentId: number): Observable<Object> =>
    this.http.delete(`${env.servicingTransferApi.loanDocuments}/${documentId}`);

  downloadLoanDocument = (documentId: number): Observable<FileObject> =>
    this.getFile(`${env.servicingTransferApi.loanDocuments}/${documentId}/download`).pipe(map(mapToFile));

  uploadLoanDocuments(loanId: number, documents: UploadDocument[]): Observable<void> {
    const formData: FormData = new FormData();
    formData.append('loanId', `${loanId}`);

    documents.forEach(({ file, type }, index) => {
      formData.append(`importFiles[${index}].Type`, type);
      formData.append(`importFiles[${index}].File`, file ?? '');
    });

    return this.http.post<void>(env.servicingTransferApi.loanDocuments, formData);
  }

  reprocessDocumentsByOrderId = (orderId: number): Observable<void> =>
    this.http.post<void>(`${env.servicingTransferApi.loanDocuments}/reprocess/orders/${orderId}`, null);

  reprocessDocumentsByLoanId = (loanId: number): Observable<void> =>
    this.http.post<void>(`${env.servicingTransferApi.loanDocuments}/reprocess/loans/${loanId}`, null);

  /** Retries the failed documents matching the Original Documents `filters`. */
  reprocessFilteredDocuments = (orderId: number, filters: string): Observable<{ count: number }> =>
    this.http.post<{ count: number }>(`${env.servicingTransferApi.loanDocuments}/reprocess/orders/${orderId}`, null, {
      params: { filters }
    });

  reprocessDocumentsByIds = (loanDocumentIds: number[]): Observable<void> =>
    this.http.post<void>(`${env.servicingTransferApi.loanDocuments}/reprocess`, { loanDocumentIds });

  getDocumentInfoWithActivities = (documentId: number): Observable<Document> =>
    this.http.get<Document>(`${env.servicingTransferApi.loanDocuments}/${documentId}/with-activities`);

  getLoanInfoWithActivities = (loanId: number): Observable<LoanDocumentWithActivity> =>
    this.http.get<LoanDocumentWithActivity>(`${env.servicingTransferApi.loanDocuments}/${loanId}/all-with-activities`);

  sendIndexingResultsForLoan = (loanId: number): Observable<void> =>
    this.http.post<void>(`${env.servicingTransferApi.loans}/${loanId}/post-indexing-results`, {});

  // Loans
  getLoanInfo = (loanId: number): Observable<Loan> =>
    this.http.get<Loan>(`${env.servicingTransferApi.loans}/${loanId}`);

  getLoans = (request: GetLoansRequest): Observable<PagedData<LoanListItem>> => {
    const queryParams = buildQueryParams(request);

    return this.http.get<PagedData<LoanListItem>>(`${env.servicingTransferApi.loans}${queryParams}`);
  };

  getLoanStatusCounts = (request: GetLoansRequest): Observable<LoanStatusCount[]> => {
    const queryParams = buildQueryParams(request);

    return this.http.get<LoanStatusCount[]>(`${env.servicingTransferApi.loans}/status-counts${queryParams}`);
  };

  exportLoansToExcel = (request: GetLoansRequest): Observable<FileObject> => {
    const queryParams = buildQueryParams(request);

    return this.http
      .get(`${env.servicingTransferApi.loans}/export-to-excel${queryParams}`, {
        observe: 'response',
        responseType: 'blob'
      })
      .pipe(map(mapToFile));
  };

  getDocumentsByOrder = (request: GetLoansRequest): Observable<PagedData<OrderLoanDocument>> => {
    const queryParams = buildQueryParams(request);

    return this.http.get<PagedData<OrderLoanDocument>>(
      `${env.servicingTransferApi.orderDocuments}/paginated${queryParams}`
    );
  };

  getArchivesWithInnerFiles = (request: GetLoansRequest): Observable<PagedData<OrderArchive>> => {
    const queryParams = buildQueryParams(request);

    return this.http.get<PagedData<OrderArchive>>(`${env.servicingTransferApi.documentFiles}${queryParams}`);
  };

  getIndexedFiles = (request: GetLoansRequest): Observable<PagedData<IndexedDocument>> => {
    const queryParams = buildQueryParams(request);

    return this.http.get<PagedData<IndexedDocument>>(
      `${env.servicingTransferApi.documentFiles}/indexed-files${queryParams}`
    );
  };

  /** Non-zero section counts per Blitz status, by wire value ("SendFailed"). */
  getIndexedDeliveryStatusCounts = ({ orderId, filters }: GetLoansRequest): Observable<LoanStatusCount[]> => {
    const queryParams = buildQueryParams({ orderId, filters });
    return this.http.get<LoanStatusCount[]>(
      `${env.servicingTransferApi.documentFiles}/indexed-files/delivery-status-counts${queryParams}`
    );
  };

  getExtractedFiles = (request: GetLoansRequest) => {
    const queryParams = buildQueryParams(request);

    return this.http.get<PagedData<ExtractedDocument>>(
      `${env.servicingTransferApi.documentFiles}/extractions${queryParams}`
    );
  };

  exportExtractedFilesToExcel = (request: GetLoansRequest): Observable<FileObject> => {
    const queryParams = buildQueryParams(request);
    return this.http
      .get(`${env.servicingTransferApi.documentFiles}/extractions/export-to-excel${queryParams}`, {
        observe: 'response',
        responseType: 'blob'
      })
      .pipe(map(mapToFile));
  };

  exportDocuments = (request: Partial<GetLoansRequest>) => {
    const queryParams = buildQueryParams(request);

    return this.http
      .get(`${env.servicingTransferApi.documentFiles}/export-to-excel${queryParams}`, {
        observe: 'response',
        responseType: 'blob'
      })
      .pipe(map(mapToFile));
  };

  /** A section's Blitz attempts, newest first, Blocked and backfill rows included. */
  getDeliveryAttempts = (documentId: number): Observable<DeliveryAttempt[]> =>
    this.http.get<DeliveryAttempt[]>(`${env.servicingTransferApi.documentFiles}/indexed-files/${documentId}/delivery-attempts`);

  exportIndexedFiles = (request: Partial<GetLoansRequest>) => {
    const queryParams = buildQueryParams(request);

    return this.http
      .get(`${env.servicingTransferApi.documentFiles}/indexed-files/export-to-excel${queryParams}`, {
        observe: 'response',
        responseType: 'blob'
      })
      .pipe(map(mapToFile));
  };

  /** Sends these indexed documents (section Doc Ids) only, not their sibling sections. */
  sendIndexedDocuments = (documentIds: number[]): Observable<void> =>
    this.http.post<void>(`${env.servicingTransferApi.documentFiles}/indexed-files/send`, { documentIds });

  /** Sends every indexed document of the order matching `filters` (the Indexed Documents grid's). */
  sendFilteredIndexedDocuments = (orderId: number, filters: string): Observable<void> =>
    this.http.post<void>(`${env.servicingTransferApi.documentFiles}/indexed-files/send-filtered`, { orderId, filters });

  /** Admin only: forced re-send of one already-sent or pre-cutover section. */
  resendIndexedDocument = (documentId: number): Observable<void> =>
    this.http.post<void>(`${env.servicingTransferApi.documentFiles}/indexed-files/${documentId}/resend`, {});

  getDocumentStatusCounts = ({ orderId, filters }: GetLoansRequest): Observable<LoanStatusCount[]> => {
    const queryParams = buildQueryParams({ orderId, filters });
    return this.http.get<LoanStatusCount[]>(`${env.servicingTransferApi.documentFiles}/status-counts${queryParams}`);
  };

  // Mapping Templates
  createMappingTemplate = (request: CreateMappingTemplateRequest): Observable<void> =>
    this.http.post<void>(env.servicingTransferApi.mappingTemplates, request);

  getMappingTemplates = (mappingDirection: MappingDirection): Observable<ImportTemplate[]> =>
    this.http.get<ImportTemplate[]>(
      `${env.servicingTransferApi.mappingTemplates}?mappingDirection=${mappingDirection}`
    );

  getMappingTemplateById = (templateId: number): Observable<ImportTemplateWithMappings> =>
    this.http.get<ImportTemplateWithMappings>(`${env.servicingTransferApi.mappingTemplates}/${templateId}`);

  deleteMappingTemplate = (templateId: number): Observable<Object> =>
    this.http.delete(`${env.servicingTransferApi.mappingTemplates}/${templateId}`);

  updateMappingTemplate = (templateId: number, columnMappings: ColumnMapping[], name?: string): Observable<void> =>
    this.http.put<void>(`${env.servicingTransferApi.mappingTemplates}/${templateId}`, { columnMappings, name });

  // Order Documents
  exportData = (orderDocumentId: number): Observable<number> =>
    this.http.post<number>(`${env.servicingTransferApi.orderDocuments}/${orderDocumentId}/export-data`, null);

  removeOrderDocument = (orderDocumentId: number): Observable<Object> =>
    this.http.delete(`${env.servicingTransferApi.orderDocuments}/${orderDocumentId}`);

  downloadOrderDocument = (orderDocumentId: number): Observable<FileObject> =>
    this.getFile(`${env.servicingTransferApi.orderDocuments}/${orderDocumentId}/download`).pipe(map(mapToFile));

  downloadMappingMetadataForDocument = (orderDocumentId: number): Observable<FileObject> =>
    this.getFile(`${env.servicingTransferApi.columnMappings}/download?orderDocumentId=${orderDocumentId}`).pipe(
      map(mapToFile)
    );

  getOrderDocumentById = (orderDocumentId: number): Observable<OrderDocument> =>
    this.http.get<OrderDocument>(`${env.servicingTransferApi.orderDocuments}/${orderDocumentId}`);

  getOrderDocuments<T extends DataFile | DocumentFileRow | OnboardingFileRow | PayHistoryFileRow>(
    orderId: number,
    documentType: OrderDocumentType
  ): Observable<T[]> {
    return this.http
      .get<T[]>(`${env.servicingTransferApi.orderDocuments}?orderId=${orderId}&type=${documentType}`)
      .pipe(map((documents) => documents.map((document) => ({ ...document, id: document.orderDocumentId }))));
  }

  uploadOrderDocuments(
    orderId: number,
    documents: UploadDocument[],
    orderDocumentIdsToDelete: number[] = []
  ): Observable<number> {
    const formData: FormData = new FormData();
    formData.append('orderId', `${orderId}`);
    if (orderDocumentIdsToDelete.length) {
      formData.append('orderDocumentIdsToDelete', orderDocumentIdsToDelete.join(','));
    }

    documents.forEach(({ delimiter, file, type }, index) => {
      formData.append(`importFiles[${index}].Delimiter`, delimiter ?? DelimiterType.Excel);
      formData.append(`importFiles[${index}].Type`, type);
      formData.append(`importFiles[${index}].File`, file ?? '');
    });

    return this.http.post<number>(env.servicingTransferApi.orderDocuments, formData).pipe(map(() => orderId));
  }

  reuploadOrderDocument(orderDocumentId: number, file: File): Observable<number> {
    const formData: FormData = new FormData();
    formData.append('file', file ?? '');

    return this.http.patch<number>(`${env.servicingTransferApi.orderDocuments}/${orderDocumentId}/reupload`, formData);
  }

  importData = (orderDocumentId: number): Observable<DealImportResults> =>
    this.http.post<DealImportResults>(
      `${env.servicingTransferApi.orderDocuments}/${orderDocumentId}/import-data`,
      null
    );

  // Orders
  createOrderByStorage = (body: CreateOrdersByStorageRequest) =>
    this.http.post<number>(`${env.servicingTransferApi.orders}/by-storage`, body);

  createOrderByOnBoardingFile = ({
    name,
    onboardingFile,
    channelName,
    reportsOutputDirectory
  }: CreateOrdersByOnboardingFileRequest) => {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('onboardingFile.file', onboardingFile);
    formData.append('onboardingFile.type', OrderDocumentType.OnboardingFile);
    formData.append('onboardingFile.delimiter', 'None');
    if (channelName) {
      formData.append('channelName', channelName);
    }
    if (reportsOutputDirectory) {
      formData.append('reportsOutputDirectory', reportsOutputDirectory);
    }

    return this.http.post<number>(`${env.servicingTransferApi.orders}/by-onboarding-file`, formData);
  };

  exportReconciliationReport = (orderId: number, date: string): Observable<FileObject> => {
    const queryParams = buildQueryParams({ orderId, date });
    return this.http
      .get(`${env.servicingTransferApi.reconciliationExport}${queryParams}`, {
        observe: 'response',
        responseType: 'blob'
      })
      .pipe(map(mapToFile));
  };

  scanOrder = (orderId: number) => this.http.patch(`${env.servicingTransferApi.orders}/${orderId}/scan`, {});

  /** `filters` limits the send to matching grid rows. */
  sendIndexingResultsForOrder = (orderId: number, filters?: string): Observable<void> =>
    this.http.post<void>(`${env.servicingTransferApi.orders}/${orderId}/indexing-results`, {}, {
      params: filters ? { filters } : {}
    });

  exportIndexingReports = (
    orderId: number,
    body: ExportIndexingReportsRequest
  ): Observable<ExportIndexingReportsResponse> =>
    this.http.post<ExportIndexingReportsResponse>(
      `${env.servicingTransferApi.orders}/${orderId}/delivery-exports/indexing-reports`,
      body
    );

  getIndexingReports = (orderId: number): Observable<IndexingReportSummary[]> =>
    this.http.get<IndexingReportSummary[]>(
      `${env.servicingTransferApi.orders}/${orderId}/indexing-reports`
    );

  /** Downloads an existing file; `exportId` selects an older one. */
  downloadIndexingReport = (orderId: number, reportKey: string, exportId?: number): Observable<FileObject> => {
    const queryParams = exportId == null ? '' : buildQueryParams({ exportId });

    return this.http
      .get(
        `${env.servicingTransferApi.orders}/${orderId}/indexing-reports/${reportKey}/download${queryParams}`,
        { observe: 'response', responseType: 'blob' }
      )
      .pipe(map(mapToFile));
  };

  getOrders = (request: GetOrdersRequest): Observable<PagedData<ServicingTransferOrder>> => {
    const queryParams = buildQueryParams(request);

    return this.http.get<PagedData<ServicingTransferOrder>>(`${env.servicingTransferApi.orders}${queryParams}`);
  };

  exportOrders = (request: Partial<GetLoansRequest>) => {
    const queryParams = buildQueryParams(request);

    return this.http
      .get(`${env.servicingTransferApi.orders}/export-to-excel${queryParams}`, {
        observe: 'response',
        responseType: 'blob'
      })
      .pipe(map(mapToFile));
  };

  editOrder = (request: EditOrdersRequest) => this.http.put(env.servicingTransferApi.orders, request);

  getOrder = (orderId: number) =>
    this.http.get<ServicingTransferOrderDetail>(`${env.servicingTransferApi.orders}/${orderId}`);

  protected getFile<T>(url: string, httpParams?: HttpParams): Observable<any> {
    return this.http.get(`${url}`, { observe: 'response', responseType: 'blob', params: httpParams });
  }
}
